import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ESignRecipient, ESignRecipientAPIFormat, SaveEsignDatas } from "src/api/contract/contract-types";
import { BaseButton, BaseModal } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { BaseStep } from "src/components/BaseStep";
import { parsedPhoneNumber } from "src/utils";
import useEsignApi from "../../hooks/useEsignApi";
import EsignStepOne from "./components/EsignStepOne";
import EsignStepThree from "./components/EsignStepThree";
import EsignStepTwo from "./components/EsignStepTwo";

/**
 * 전자서명 정보 생성 모달의 props 인터페이스
 * @param setIsSaved 저장 상태 설정 함수
 * @param flags 모달 표시 여부
 * @param setEditable 편집 가능 상태 설정 함수
 * @param setESignFlags 전자서명 상태 설정 함수
 * @param initDatas 데이터 초기화 함수
 * @param initMainData 메인 데이터 초기화 함수
 * @param setIsEsign 전자서명 상태 설정 함수
 * @param setData 데이터 설정 함수
 * @param onClose 모달 닫기 콜백 함수
 * @param onClick 클릭 콜백 함수
 */
interface Props {
  setIsSaved: any;
  flags: boolean;
  setEditable: any;
  setESignFlags: any;
  initDatas: any;
  initMainData?: any;
  setIsEsign?: any;
  setData?: any;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * 전자서명 단계 인터페이스
 * @param stepNumber 단계 번호
 * @param label 단계 라벨
 * @param dataCheck 데이터 검증 여부
 * @param children 단계 컴포넌트
 */
interface Step {
  stepNumber: number;
  label: string;
  dataCheck: boolean;
  children: React.ReactNode;
}

/**
 * 전자서명 정보 생성 모달 컴포넌트
 * 전자서명을 위한 정보를 생성하고 설정하는 모달 컴포넌트입니다.
 * 서식 선택, 문서 설정, 확인 및 전송 단계로 구성되어 있습니다.
 * @param flags 모달 표시 여부
 * @param setIsSaved 저장 상태 설정 함수
 * @param setESignFlags 전자서명 상태 설정 함수
 * @param initDatas 데이터 초기화 함수
 * @param setEditable 편집 가능 상태 설정 함수
 * @param initMainData 메인 데이터 초기화 함수
 * @param setIsEsign 전자서명 상태 설정 함수
 * @param setData 데이터 설정 함수
 */
const MakeEsignInfo = ({ flags, setIsSaved, setESignFlags, initDatas, setEditable, initMainData, setIsEsign, setData }: Props) => {
  const { id } = useParams();
  const { getMemberInfo, getWritedPreviewData, confirmEsignData } = useEsignApi();
  // 선택된 서식
  const [selectedTemplate, setSeletedTemplate] = useState<any>({});
  // 저장 상태
  const [isSave, setIsSave] = useState(false);
  // 검증 통과 여부
  const [isPassed, setIsPassed] = useState(false);
  // 버튼 비활성화 상태
  const [btnDisabled, setBtnDisabled] = useState(false);
  // 전송자 정보
  const [sendUser, setSendUser] = useState<any>({});
  // 전자서명 워크플로우 데이터
  const [workflow, setWorkflow] = useState<SaveEsignDatas>({});
  // 작성 URL
  const [writeUrl, setWriteUrl] = useState("");

  // 전자서명 단계 정의
  const steps: Array<Step> = [
    {
      stepNumber: 0,
      label: "서식 선택",
      dataCheck: false,
      children: (
        <EsignStepOne selectedTemplate={selectedTemplate} setSeletedTemplate={setSeletedTemplate} workflow={workflow} setWorkflow={setWorkflow} />
      ),
    },
    {
      stepNumber: 1,
      label: "문서 설정",
      dataCheck: false,
      children: (
        <EsignStepTwo
          selectedTemplate={selectedTemplate}
          setSeletedTemplate={setSeletedTemplate}
          sendUser={sendUser}
          workflow={workflow}
          setWorkflow={setWorkflow}
        />
      ),
    },
    {
      stepNumber: 2,
      label: "확인 및 전송",
      dataCheck: false,
      children: <EsignStepThree sendUser={sendUser} workflow={workflow} isPassed={isPassed} setIsPassed={setIsPassed} writeUrl={writeUrl} />,
    },
  ];

  // 모달 표시 상태
  const [isOpen, setIsOpen] = useState(flags);
  // 현재 활성화된 단계
  const [activeTab, setActiveTab] = useState(0);

  /**
   * 전자서명 데이터 형식 변환 함수
   * @param saveData 저장할 전자서명 데이터
   * @returns 변환된 전자서명 데이터
   */
  const changeListObject = (saveData: SaveEsignDatas) => {
    const list = saveData.recipientList!;
    const arr: Array<ESignRecipientAPIFormat> = [];
    for (let i = 0; i <= list.length - 1; i++) {
      const ar: ESignRecipientAPIFormat = {
        order: Number(list[i].order),
        name: String(list[i].name),
        email: String(list[i].email),
      };
      if (list[i].mobile) {
        ar.mobile = parsedPhoneNumber(list[i].mobile!);
      } else {
        ar.mobile = "";
      }
      if (list[i].password) {
        ar.password = list[i].password;
      } else {
        ar.password = "";
      }
      if (list[i].passwordHint) {
        ar.passwordHint = list[i].passwordHint;
      } else {
        ar.passwordHint = "";
      }
      arr.push(ar);
    }
    saveData.recipientList = arr;
    return saveData;
  };

  /**
   * 전자서명 확인 및 저장 함수
   * @param workflow 전자서명 워크플로우 데이터
   */
  const confirmSign = useCallback(
    async (workflow: SaveEsignDatas) => {
      const saveData = changeListObject(workflow);
      const sendData = { contractId: id, workflow: saveData };
      const response: any = await confirmEsignData(sendData);

      setIsSaved(true);
      setESignFlags(false);
      setIsEsign(true);
      setEditable(false);
      initDatas();
      initMainData();
      setData(String(id));
    },
    [confirmEsignData, id, initDatas, setESignFlags, setEditable, setIsSaved, initMainData],
  );

  /**
   * 로그인한 전자서명 회원 정보 조회 함수
   */
  const getLoginEsignMemeber = useCallback(async () => {
    const response = await getMemberInfo();
    setSendUser(response.data.data.esignonMember);
  }, [getMemberInfo]);

  /**
   * 작성된 문서 미리보기 URL 조회 함수
   * @param workflow 전자서명 워크플로우 데이터
   */
  const getWritedPreview = useCallback(
    async (workflow: SaveEsignDatas) => {
      const saveData = changeListObject(workflow);
      const url = await getWritedPreviewData({ contractId: id, workflow: saveData });
      setWriteUrl(url);
    },
    [getWritedPreviewData, id],
  );

  // 선택된 서식이 변경될 때 워크플로우 데이터 업데이트
  useEffect(() => {
    getLoginEsignMemeber();
    if (selectedTemplate && selectedTemplate.templateId) {
      setWorkflow({ ...workflow, ...{ templateId: selectedTemplate.templateId } });
    }
  }, [selectedTemplate, setSeletedTemplate]);

  // 모달 표시 상태 업데이트
  useEffect(() => {
    if (flags === true) {
      setIsOpen(flags);
    }
  }, [flags]);

  // 현재 단계에 따른 버튼 활성화 상태 업데이트
  useEffect(() => {
    if (activeTab === 0) {
      if (selectedTemplate && selectedTemplate.templateId) {
        setBtnDisabled(false);
      } else {
        setBtnDisabled(true);
      }
    }
    if (activeTab === 1) {
      setBtnDisabled(checkDataforDisable);
    }
    if (activeTab === 2) {
      setBtnDisabled(false);
    }
  }, [activeTab, selectedTemplate]);

  // 워크플로우 데이터 변경 시 버튼 활성화 상태 업데이트
  useEffect(() => {
    if (activeTab === 1) {
      setBtnDisabled(checkDataforDisable);
    }
  }, [workflow]);

  /**
   * 데이터 검증 함수
   * 전자서명 데이터의 필수 필드와 형식을 검증합니다.
   * @returns 검증 결과
   */
  const checkDataforDisable = useMemo(() => {
    let flag = false;
    const checkPhone = /^\d{2,3}-?\d{3,4}-?\d{4}$/;
    const checkEmail = /([\w-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;
    if (workflow.recipientList === undefined) {
      flag = true;
    } else {
      workflow.recipientList!.map((recipient: ESignRecipient) => {
        if (recipient.name === undefined || recipient.name === "") {
          flag = true;
        }
        if (recipient.email === undefined || recipient.email === "") {
          flag = true;
        } else {
          if (checkPhone.test(recipient.email!) === false && checkEmail.test(recipient.email!) === false) {
            flag = true;
          }
        }
      });
    }
    return flag;
  }, [workflow]);

  return (
    <>
      {isOpen && (
        <BaseAbstractModal size="xlarge" isOpen={isOpen}>
          <>
            <section className={`base-abstract-modal__title border-bottom flex-center-between ${activeTab === 2 ? "has-list" : ""}`}>
              <div>
                <h1>전자서명 요청</h1>
                {activeTab === 2 && (
                  <ul className="base-list mt20">
                    <li>문서 전송 후 문서명, 작성기한, 받는 사람 등을 변경하시려면 수정 후 다시 보내기 또는 새로 작성해야 합니다.</li>
                  </ul>
                )}
              </div>
              <div>
                <BaseStep stepList={["서식 선택", "문서 설정", "확인 및 전송"]} selectedStepIndex={activeTab} />
              </div>
            </section>
            <section className="base-abstract-modal__contents px30">{steps[activeTab].children}</section>

            <section className="base-abstract-modal__btn-wrap flex-center-between">
              <BaseButton
                title={"취소"}
                className="color-white"
                onClick={() => {
                  setESignFlags(false);
                }}
              />
              <div className="flex-center">
                {activeTab !== 0 && (
                  <BaseButton
                    title={"이전"}
                    className="color-white mr10"
                    onClick={() => {
                      let tab = activeTab !== 0 ? activeTab - 1 : activeTab;
                      setActiveTab(tab);
                    }}
                  />
                )}
                {activeTab >= 0 && steps.length - 1 > activeTab && (
                  <BaseButton
                    title={"다음"}
                    disabled={btnDisabled}
                    onClick={() => {
                      if (activeTab === 0) {
                        setActiveTab(activeTab + 1);
                      } else if (activeTab === 1) {
                        setActiveTab(activeTab + 1);
                        getWritedPreview(workflow);
                      }
                    }}
                  />
                )}
                {activeTab === steps.length - 1 && (
                  <BaseButton
                    title={"전송"}
                    disabled={btnDisabled}
                    onClick={() => {
                      confirmSign(workflow);
                    }}
                  />
                )}
              </div>
            </section>
          </>
        </BaseAbstractModal>
      )}
    </>
  );
};

export default MakeEsignInfo;
